// SPDX-License-Identifier: LicenseRef-Proprietary
import React, { useEffect, useState } from 'react';
import { AuthService } from '../../services/authService';
import { LoginButton } from './LoginButton';
import { PendingApprovalPage } from './PendingApprovalPage';

// Environment detection
const isDev =
  process.env.REACT_APP_ENVIRONMENT?.toLowerCase() === 'dev' ||
  process.env.NODE_ENV === 'development';
const CALLBACK_PATH = process.env.REACT_APP_AUTH_CALLBACK_PATH || '/auth/callback';
const SIGNUP_PATH = '/signup';

// Auth state types
type AuthState = 'loading' | 'authenticated' | 'unauthenticated' | 'pending_approval';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

// Helper function to get a cookie value by name
const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

// Check if user is approved based on the ehta_approved cookie set by backend
const checkUserApproval = (): boolean => {
  // Check the ehta_approved cookie set by the backend during authentication
  const approvedCookie = getCookie('ehta_approved');

  // User is approved if cookie value is "true" or cookie doesn't exist (backwards compatibility)
  // Cookie value "false" means user needs admin approval
  if (approvedCookie === 'false') {
    return false;
  }

  // If cookie is "true" or not set, user is approved
  return true;
};

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  fallback = (
    <div className="auth-guard-fallback">
      <h2>Authentication Required</h2>
      <p>Please sign in to access this application.</p>
      <LoginButton className="auth-guard-login" />
    </div>
  )
}) => {
  const [authState, setAuthState] = useState<AuthState>('loading');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Check for auth_error query param (e.g., account_pending from Cognito)
        const urlParams = new URLSearchParams(window.location.search);
        const authError = urlParams.get('auth_error');

        if (authError === 'account_pending') {
          // Clear the query param from URL to avoid showing the message on refresh
          window.history.replaceState({}, '', window.location.pathname);
          // Store in session so user sees the message
          sessionStorage.setItem('account_pending', 'true');
          setAuthState('pending_approval');
          return;
        }

        // Handle signup path - redirect to backend signup endpoint
        if (window.location.pathname === SIGNUP_PATH) {
          const signupUrl = isDev ? '/auth/signup' : '/api/auth/signup';
          window.location.replace(signupUrl);
          return;
        }

        // NOTE: We no longer short-circuit on sessionStorage here.
        // Instead, we check the actual approval status after authentication
        // to allow users to retry login after admin approves them.

        // Check if authentication is enabled
        const authEnabled = await AuthService.isAuthEnabled();

        // In production, never silently downgrade to dev mode
        if (!isDev && !authEnabled) {
          throw new Error('Auth is disabled by server in production');
        }

        if (!authEnabled) {
          console.log('Authentication not enabled - running in development mode');
          setAuthState('authenticated');
          return;
        }

        // ✅ In production, the server (/api/auth/callback) handles the code exchange
        //    and sets HttpOnly cookies. The SPA should NOT do anything here.
        if (window.location.pathname === CALLBACK_PATH) {
          if (isDev) {
            // dev-only: allow client to process callback if you use a front-channel flow locally
            const success = await AuthService.handleCallback();
            setAuthState(success ? 'authenticated' : 'unauthenticated');
            if (success) window.location.replace('/');
            return;
          } else {
            // prod: just bounce to home; server already handled cookies
            window.location.replace('/');
            return;
          }
        }

        // Check authentication status
        const authenticated = await AuthService.isAuthenticated();

        if (authenticated) {
          // Check if user is approved by admin (reads ehta_approved cookie)
          const isApproved = checkUserApproval();
          if (!isApproved) {
            sessionStorage.setItem('account_pending', 'true');
            setAuthState('pending_approval');
            return;
          }
          // User is authenticated and approved - clear any stale pending state
          sessionStorage.removeItem('account_pending');
          setAuthState('authenticated');
          return;
        }

        // Not authenticated - check if there was a previous pending state
        if (sessionStorage.getItem('account_pending') === 'true') {
          setAuthState('pending_approval');
          return;
        }

        setAuthState('unauthenticated');
      } catch (error) {
        console.error('Authentication check failed:', error);
        // In production, show error instead of silently failing
        if (!isDev) {
          setAuthState('unauthenticated');
          return;
        }
        // Dev fallback
        setAuthState('authenticated');
      }
    };

    checkAuth();
  }, []);

  // Show loading state while checking authentication
  if (authState === 'loading') {
    return (
      <div className="auth-guard-loading">
        <p>Checking authentication...</p>
      </div>
    );
  }

  // Show pending approval page if account is pending
  if (authState === 'pending_approval') {
    return <PendingApprovalPage />;
  }

  // Show fallback if not authenticated
  if (authState === 'unauthenticated') {
    return <>{fallback}</>;
  }

  // Show protected content if authenticated
  return <>{children}</>;
};
